import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog'
import { Button } from './ui/button'

// Idle-session timeout, shared by every app shell so the behaviour is identical
// regardless of which app you're in or how you signed in (password or Google).
// After IDLE_MS of no interaction we surface a countdown; if it runs out the
// user is signed out. The idle timeout deliberately applies even to
// "remembered" sessions — remember-me controls persistence across browser
// restarts, not how long an unattended tab may stay authenticated.
//
// "No interaction" means none ANYWHERE in WeTook (Frame QA 2026-10-19). Two
// bugs signed people out while they were working:
//  - scrolling didn't count: the listener sat on window, but the app shell
//    scrolls inside its own panel and scroll events don't bubble; wheel and
//    mouse movement weren't counted at all;
//  - every tab ran its own timer, and signing out ends the one shared WeTook
//    session — so an idle Frame tab signed you out of the tab you were using.
// Activity is now stamped into a cookie shared by every *.wetook.net app and
// tab; a tab only warns, and only signs out, when that shared stamp is old.
const IDLE_MS = 28 * 60 * 1000   // show warning after 28 min idle
const WARNING_SECONDS = 2 * 60   // 2-min countdown before auto sign-out → 30 min total
const CHECK_MS = 15 * 1000       // how often a tab looks at the shared stamp
const STAMP_EVERY_MS = 15 * 1000 // throttle for writing the stamp
const STAMP_COOKIE = 'wetook_last_activity'

function stampDomain(): string {
  const host = window.location.hostname
  // Same rule as the backend's wetook_logged_in marker: shared across
  // *.wetook.net, host-only elsewhere (localhost, standalone deploys).
  return host.endsWith('.wetook.net') ? '; Domain=.wetook.net' : ''
}

function readStamp(): number {
  try {
    const raw = document.cookie.split('; ').find(c => c.startsWith(`${STAMP_COOKIE}=`))
    const n = raw ? Number(raw.slice(STAMP_COOKIE.length + 1)) : NaN
    return Number.isFinite(n) ? n : 0
  } catch {
    return 0
  }
}

function writeStamp(ms: number): void {
  try {
    document.cookie = `${STAMP_COOKIE}=${ms}; Path=/; Max-Age=86400; SameSite=Lax${stampDomain()}`
  } catch {
    /* cookies blocked — this tab still tracks its own activity */
  }
}

export function SessionManager() {
  const { t } = useTranslation()
  const { user, logout } = useAuth()
  const [warning, setWarning] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(WARNING_SECONDS)

  // Use a ref bag so closures inside the effect always call the latest versions.
  const refs = useRef({ logout, markActive: () => {} })
  refs.current.logout = logout

  useEffect(() => {
    if (!user) return

    let localLast = Date.now()
    let lastWrite = 0
    let warnUntil = 0   // countdown deadline while the warning is showing

    const lastActivity = () => Math.max(localLast, readStamp())

    const markActive = () => {
      const now = Date.now()
      localLast = now
      if (now - lastWrite >= STAMP_EVERY_MS) {
        lastWrite = now
        writeStamp(now)
      }
      if (warnUntil) {
        warnUntil = 0
        setWarning(false)
      }
    }
    refs.current.markActive = markActive
    markActive()

    const tick = () => {
      const idleFor = Date.now() - lastActivity()
      if (idleFor < IDLE_MS) {
        // Active here or in another WeTook tab/app — no warning.
        if (warnUntil) { warnUntil = 0; setWarning(false) }
        return
      }
      if (!warnUntil) {
        warnUntil = Date.now() + WARNING_SECONDS * 1000
        setWarning(true)
      }
      const left = Math.max(0, Math.round((warnUntil - Date.now()) / 1000))
      setSecondsLeft(left)
      if (left <= 0) {
        warnUntil = 0
        setWarning(false)
        refs.current.logout()   // fire-and-forget; UI clears immediately
      }
    }

    // Once a second while warning (the countdown), otherwise every CHECK_MS.
    const timer = setInterval(tick, CHECK_MS)
    let fast: ReturnType<typeof setInterval> | null = null
    const syncCadence = () => {
      if (warnUntil && !fast) fast = setInterval(tick, 1000)
      if (!warnUntil && fast) { clearInterval(fast); fast = null }
    }
    const cadence = setInterval(syncCadence, 1000)

    const onActivity = () => markActive()
    const events = ['mousedown', 'keydown', 'touchstart', 'wheel', 'pointermove'] as const
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }))
    // Capture phase: scroll doesn't bubble, and the shell scrolls in a panel.
    window.addEventListener('scroll', onActivity, { passive: true, capture: true })

    return () => {
      clearInterval(timer)
      clearInterval(cadence)
      if (fast) clearInterval(fast)
      events.forEach(e => window.removeEventListener(e, onActivity))
      window.removeEventListener('scroll', onActivity, { capture: true } as EventListenerOptions)
    }
  }, [user]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!user || !warning) return null

  const minutes = Math.floor(secondsLeft / 60)
  const seconds = secondsLeft % 60
  const timeStr = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`

  return (
    <Dialog open>
      {/* The default z-50 overlay already covers the z-40 TopBar, but the
          session-expiry prompt keeps z-[70] on both overlay and content so it
          blankets the whole screen above any other chrome, no matter what. */}
      <DialogContent
        className="max-w-sm z-[70]"
        overlayClassName="z-[70]"
        onInteractOutside={e => e.preventDefault()}
        onEscapeKeyDown={e => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t('session.expiringTitle', 'Session expiring soon')}</DialogTitle>
          <DialogDescription>
            {t('session.expiringBody', "You've been inactive. You'll be signed out automatically in")}{' '}
            <span className="font-semibold text-foreground tabular-nums">{timeStr}</span>.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => refs.current.logout()}>
            {t('session.signOutNow', 'Sign out now')}
          </Button>
          <Button onClick={() => refs.current.markActive()}>
            {t('session.staySignedIn', 'Stay signed in')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default SessionManager
